/**
 * Прогресс по навыкам профессии (ТЗ 0071).
 *
 * 🔴 Пять чисел приходят с сервера одной функцией на все экраны — главную,
 * каталог, паспорт и карту. Клиент их не складывает и не выводит одно из
 * другого: «подтверждено школой» — подмножество «соответствует», а не
 * слагаемое, и сумма этих двух — ровно та вторая правда, из-за которой
 * «10 из 22» на главной спорило с картой.
 */
export interface ISkillProgress {
  /** Обязательные навыки профессии, без дополнительных. */
  requiredCount: number;
  /** Уровень в профиле не ниже требуемого. */
  meetsCount: number;
  /** Навык есть, уровень ниже требуемого. */
  improveCount: number;
  /** Навыка нет или он скрыт из профиля. */
  missingCount: number;
  /** Из «соответствует» — подтверждённые школой. */
  verifiedCount: number;
}

/** Положение навыка относительно требования профессии. */
export type RequirementStatus = 'meets' | 'improve' | 'missing';

export interface ISkillGap {
  slug: string;
  name: string;
  status: RequirementStatus;
  /** 'core' | 'important' */
  priority: string;
  requiredLevel: number;
  myLevel: number | null;
}

interface IRawSkillProgress {
  requiredCount: number;
  meetsRequiredLevelCount: number;
  needsImprovementCount: number;
  missingCount: number;
  verifiedMeetsRequiredLevelCount: number;
}

export const EMPTY_SKILL_PROGRESS: ISkillProgress = {
  requiredCount: 0,
  meetsCount: 0,
  improveCount: 0,
  missingCount: 0,
  verifiedCount: 0,
};

export const transformSkillProgress = (raw: IRawSkillProgress): ISkillProgress => ({
  requiredCount: raw.requiredCount,
  meetsCount: raw.meetsRequiredLevelCount,
  improveCount: raw.needsImprovementCount,
  missingCount: raw.missingCount,
  verifiedCount: raw.verifiedMeetsRequiredLevelCount,
});

/**
 * Источники выполненных требований (ТЗ 0073, 5.1) — две НЕПЕРЕСЕКАЮЩИЕСЯ части.
 *
 * Сервер считает «подтверждено школой» только у навыка, который уже
 * соответствует требованию и пришёл с курса, а у навыка один источник. Поэтому
 * остаток `meets − verified` — ровно навыки по самооценке, и один навык не
 * попадает в обе части; при подтверждении школы источником называется она.
 */
export const progressSources = (progress: ISkillProgress) => ({
  verified: progress.verifiedCount,
  selfAssessed: Math.max(progress.meetsCount - progress.verifiedCount, 0),
});

/** Неизвестное значение с сервера трактуем как «не хватает», а не как «есть». */
export const toRequirementStatus = (value: string): RequirementStatus =>
  value === 'meets' || value === 'improve' ? value : 'missing';
